<template>
  <div class="ss-root flex h-screen min-h-0 w-full flex-col overflow-hidden text-white antialiased md:flex-row">
    <PlusAppSidebar />

    <div class="ss-shell relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden" :data-theme="theme">
      <header class="ss-topbar shrink-0">
        <div class="ss-topbar__left">
          <button type="button" class="ss-icon-btn" title="返回" @click="goHome">
            <i class="fas fa-arrow-left"></i>
          </button>
          <div class="ss-topbar__title-wrap">
            <h1 class="ss-topbar__title">{{ displayTitle }}</h1>
            <p class="ss-topbar__sub">
              策划 → 大纲 → 角色 → 世界 → 正文
              <span v-if="draft.cloudSavedAt" class="ss-topbar__saved">已云端保存</span>
              <span v-else-if="draft.updatedAt" class="ss-topbar__saved">已本地保存</span>
            </p>
          </div>
        </div>
        <div class="ss-topbar__actions">
          <button
            type="button"
            class="ss-icon-btn ss-theme-btn"
            :title="theme === 'dark' ? '切换为浅色' : '切换为暗色'"
            @click="toggleTheme"
          >
            <i :class="theme === 'dark' ? 'fas fa-sun' : 'fas fa-moon'"></i>
          </button>
          <button type="button" class="ss-btn ss-btn--ghost" :disabled="busy || cloudSaving" @click="openCloudList">
            <i class="fas fa-folder-open"></i>
            打开
          </button>
          <button type="button" class="ss-btn ss-btn--ghost" :disabled="busy || cloudSaving" @click="onReset">
            新建
          </button>
          <button type="button" class="ss-btn ss-btn--ghost" :disabled="busy" @click="onDownload">
            <i class="fas fa-download"></i>
            导出
          </button>
          <button
            type="button"
            class="ss-btn ss-btn--outline"
            :disabled="busy || cloudSaving"
            @click="onCloudSave"
          >
            <i :class="cloudSaving ? 'fas fa-spinner fa-spin' : 'fas fa-cloud-upload-alt'"></i>
            {{ cloudSaving ? '保存中…' : '保存到云端' }}
          </button>
          <!-- <button type="button" class="ss-btn ss-btn--outline" :disabled="busy" @click="onSendToCanvas">
            <i class="fas fa-project-diagram"></i>
            送到画布
          </button> -->
        </div>
      </header>

      <!-- 云端剧本列表 -->
      <div v-if="cloudListOpen" class="ss-modal" @click.self="cloudListOpen = false">
        <div class="ss-modal__panel" role="dialog" aria-modal="true" aria-label="我的云端剧本">
          <div class="ss-modal__head">
            <div>
              <h2 class="ss-modal__title">我的云端剧本</h2>
              <p class="ss-modal__sub">选择一部已保存的剧本继续编辑</p>
            </div>
            <button type="button" class="ss-icon-btn" title="关闭" @click="cloudListOpen = false">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="ss-modal__toolbar">
            <input
              v-model.trim="cloudListKeyword"
              type="search"
              class="ss-input ss-modal__search"
              placeholder="搜索剧名"
              autocomplete="off"
            />
            <button type="button" class="ss-btn ss-btn--ghost" :disabled="cloudListLoading" @click="refreshCloudList">
              <i :class="cloudListLoading ? 'fas fa-spinner fa-spin' : 'fas fa-sync'"></i>
              刷新
            </button>
          </div>

          <div class="ss-modal__body scrollbar-thin">
            <p v-if="cloudListLoading" class="ss-modal__empty">
              <i class="fas fa-spinner fa-spin"></i>
              加载中…
            </p>
            <p v-else-if="cloudListError" class="ss-modal__empty ss-modal__empty--err">
              {{ cloudListError }}
            </p>
            <div v-else-if="!filteredCloudList.length" class="ss-modal__empty">
              <p>暂无云端剧本</p>
              <p class="ss-modal__hint">
                请先在编辑页点「保存到云端」。若已保存仍为空，请确认：
                <br />1）后端已同步接口；2）已执行
                <code>script_studio_projects.sql</code>
              </p>
              <button
                type="button"
                class="ss-btn ss-btn--outline ss-modal__save-now"
                :disabled="cloudSaving"
                @click="onCloudSaveFromModal"
              >
                <i :class="cloudSaving ? 'fas fa-spinner fa-spin' : 'fas fa-cloud-upload-alt'"></i>
                保存当前剧本到云端
              </button>
            </div>
            <ul v-else class="ss-cloud-list">
              <li v-for="row in filteredCloudList" :key="row.uuid" class="ss-cloud-item">
                <button type="button" class="ss-cloud-item__main" @click="openCloudProject(row)">
                  <span class="ss-cloud-item__thumb">{{ cloudTitleInitials(row.title) }}</span>
                  <span class="ss-cloud-item__meta">
                    <span class="ss-cloud-item__title">{{ row.title || '未命名剧本' }}</span>
                    <span class="ss-cloud-item__time">{{ formatCloudTime(row.updated_at) }}</span>
                  </span>
                </button>
                <button
                  type="button"
                  class="ss-cloud-item__del"
                  title="删除"
                  :disabled="cloudDeletingUuid === row.uuid"
                  @click="deleteCloudProject(row)"
                >
                  <i
                    :class="
                      cloudDeletingUuid === row.uuid ? 'fas fa-spinner fa-spin' : 'fas fa-trash-alt'
                    "
                  ></i>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="ss-body min-h-0 flex-1">
        <!-- 左：剧本信息 -->
        <aside class="ss-nav">
          <p class="ss-nav__label">剧本信息</p>
          <button
            v-for="s in sections"
            :key="s.id"
            type="button"
            class="ss-nav__item"
            :class="{ 'ss-nav__item--on': sectionId === s.id }"
            @click="sectionId = s.id"
          >
            <i :class="s.icon"></i>
            <span>{{ s.label }}</span>
          </button>

          <div class="ss-nav__hint">
            <p>创作路径</p>
            <ol>
              <li>填灵感 → 生成策划</li>
              <li>生成大纲 / 角色 / 世界</li>
              <li>分批生成剧本正文</li>
            </ol>
          </div>

          <div v-if="episodeBatches.length" class="ss-nav__batches">
            <p class="ss-nav__label">正文分批</p>
            <button
              v-for="b in episodeBatches"
              :key="b.id"
              type="button"
              class="ss-nav__item ss-nav__item--batch"
              :class="{
                'ss-nav__item--on': sectionId === 'script' && scriptBatchId === b.id,
                'ss-nav__item--done': !!episodeBodyMap[b.id]
              }"
              @click="selectEpisodeBatch(b)"
            >
              <i :class="episodeBodyMap[b.id] ? 'fas fa-check-circle' : 'fas fa-file-alt'"></i>
              <span>{{ b.label }}</span>
            </button>
          </div>

          <div class="ss-nav__foot">
            <button
              type="button"
              class="ss-cta ss-cta--primary"
              :disabled="busy"
              :title="scriptGenCreditTitle"
              @click="onGenerateCurrent"
            >
              <span class="ss-cta__main">
                <i :class="busy ? 'fas fa-spinner fa-spin' : 'fas fa-wand-magic-sparkles'"></i>
                <span class="ss-cta__text">
                  <span class="ss-cta__label">{{ generateLabel }}</span>
                  <span v-if="generateSubLabel" class="ss-cta__sub">{{ generateSubLabel }}</span>
                </span>
              </span>
              <span v-if="!busy" class="ss-credit-badge" aria-hidden="true">
                <i class="fas fa-bolt"></i>
                {{ scriptGenCreditsText }}
              </span>
            </button>
          </div>
        </aside>

        <!-- 中：内容 -->
        <main class="ss-main scrollbar-thin">
          <!-- 策划 -->
          <template v-if="sectionId === 'planning'">
            <section class="ss-card">
              <h2 class="ss-card__h">创作灵感</h2>
              <textarea
                v-model="draft.idea"
                class="ss-textarea"
                rows="3"
                placeholder="输入一句话灵感、对标剧名，或粘贴参考素材（可空，由 AI 直接选题）"
              ></textarea>
            </section>

            <section v-if="hasAiTopic" class="ss-card">
              <h2 class="ss-card__h">AI选题</h2>
              <ol class="ss-topic">
                <li><strong>剧名：</strong>《{{ draft.aiTopic.title }}》</li>
                <li><strong>种类：</strong>{{ draft.aiTopic.category }}</li>
                <li>
                  <strong>题材：</strong>
                  {{ (draft.aiTopic.themes || []).join('、') || '—' }}
                </li>
                <li><strong>一句话：</strong>{{ draft.aiTopic.oneLiner }}</li>
                <li><strong>受众：</strong>{{ draft.aiTopic.audience }}</li>
                <li><strong>内核：</strong>{{ draft.aiTopic.core }}</li>
                <li><strong>主线：</strong>{{ draft.aiTopic.mainPlot }}</li>
                <li><strong>亮点：</strong>{{ draft.aiTopic.highlights }}</li>
              </ol>
            </section>

            <section class="ss-card">
              <div class="ss-card__head">
                <h2 class="ss-card__h">剧本策划</h2>
                <span class="ss-badge">可编辑</span>
              </div>

              <div class="ss-form">
                <label class="ss-field">
                  <span>剧本名称</span>
                  <input v-model.trim="draft.plan.title" type="text" class="ss-input" />
                </label>
                <label class="ss-field ss-field--block">
                  <span>主题</span>
                  <textarea v-model="draft.plan.theme" class="ss-textarea" rows="2"></textarea>
                </label>
                <div class="ss-form__row">
                  <label class="ss-field">
                    <span>市场</span>
                    <select v-model="draft.plan.market" class="ss-input">
                      <option v-for="m in markets" :key="m.id" :value="m.id">{{ m.label }}</option>
                    </select>
                  </label>
                  <label class="ss-field">
                    <span>种类</span>
                    <select v-model="draft.plan.category" class="ss-input">
                      <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.label }}</option>
                    </select>
                  </label>
                </div>
                <label class="ss-field ss-field--block">
                  <span>题材</span>
                  <input
                    v-model.trim="draft.plan.genreText"
                    type="text"
                    class="ss-input"
                    placeholder="用顿号或逗号分隔，如：重生、复仇、甜宠"
                    @blur="syncGenresFromText"
                  />
                  <div v-if="draft.plan.genres && draft.plan.genres.length" class="ss-tags">
                    <span v-for="(g, i) in draft.plan.genres" :key="i" class="ss-tag">{{ g }}</span>
                  </div>
                </label>
                <div class="ss-form__row">
                  <label class="ss-field">
                    <span>总集数</span>
                    <div class="ss-input-unit">
                      <input v-model.number="draft.plan.episodeCount" type="number" min="1" max="200" class="ss-input" />
                      <em>集</em>
                    </div>
                  </label>
                  <label class="ss-field">
                    <span>单集时长</span>
                    <div class="ss-range">
                      <input v-model.number="draft.plan.episodeDurationMin" type="number" min="0.5" step="0.5" class="ss-input" />
                      <span>–</span>
                      <input v-model.number="draft.plan.episodeDurationMax" type="number" min="0.5" step="0.5" class="ss-input" />
                      <em>分钟</em>
                    </div>
                  </label>
                </div>
                <div class="ss-form__row">
                  <label class="ss-field">
                    <span>主要角色上限</span>
                    <div class="ss-input-unit">
                      <input v-model.number="draft.plan.maxCharacters" type="number" min="1" class="ss-input" />
                      <em>人</em>
                    </div>
                  </label>
                  <label class="ss-field">
                    <span>每集场景上限</span>
                    <div class="ss-input-unit">
                      <input v-model.number="draft.plan.maxScenesPerEpisode" type="number" min="1" class="ss-input" />
                      <em>个</em>
                    </div>
                  </label>
                </div>
                <div class="ss-form__row">
                  <label class="ss-field">
                    <span>语言</span>
                    <input v-model.trim="draft.plan.language" type="text" class="ss-input" />
                  </label>
                  <label class="ss-field">
                    <span>每集字数上限</span>
                    <div class="ss-input-unit">
                      <input v-model.number="draft.plan.maxWordsPerEpisode" type="number" min="100" class="ss-input" />
                      <em>字</em>
                    </div>
                  </label>
                </div>
                <div class="ss-form__row">
                  <label class="ss-field">
                    <span>对白密度</span>
                    <input v-model.trim="draft.plan.dialogueDensity" type="text" class="ss-input" placeholder="低 / 中 / 高" />
                  </label>
                  <label class="ss-field">
                    <span>风格格式</span>
                    <input v-model.trim="draft.plan.styleFormat" type="text" class="ss-input" />
                  </label>
                </div>
                <label class="ss-field ss-field--block">
                  <span>核心要求</span>
                  <textarea v-model="draft.plan.coreRequirements" class="ss-textarea" rows="4"></textarea>
                </label>
              </div>
            </section>
          </template>

          <!-- 剧本正文 -->
          <template v-else-if="sectionId === 'script'">
            <section class="ss-card">
              <div class="ss-card__head">
                <h2 class="ss-card__h">剧本正文</h2>
                <span class="ss-badge">按批生成 · 建议先写完策划与大纲</span>
              </div>
              <div class="ss-form__row">
                <label class="ss-field">
                  <span>起始集</span>
                  <input
                    v-model.number="draft.scriptFrom"
                    type="number"
                    min="1"
                    :max="episodeTotal"
                    class="ss-input"
                  />
                </label>
                <label class="ss-field">
                  <span>结束集</span>
                  <input
                    v-model.number="draft.scriptTo"
                    type="number"
                    min="1"
                    :max="episodeTotal"
                    class="ss-input"
                  />
                </label>
              </div>
              <div class="ss-batch-chips">
                <button
                  v-for="b in episodeBatches"
                  :key="'chip-' + b.id"
                  type="button"
                  class="ss-chip"
                  :class="{
                    'ss-chip--on': scriptBatchId === b.id,
                    'ss-chip--done': !!episodeBodyMap[b.id]
                  }"
                  @click="selectEpisodeBatch(b)"
                >
                  {{ b.id }}集
                </button>
              </div>
              <p class="ss-script-tip">
                当前将生成第 {{ normalizedScriptFrom }}–{{ normalizedScriptTo }} 集（共
                {{ episodeTotal }} 集）。单次建议不超过 5 集，避免超时。
              </p>
            </section>

            <section class="ss-card ss-card--doc">
              <div class="ss-card__head">
                <h2 class="ss-card__h">
                  第 {{ normalizedScriptFrom }}–{{ normalizedScriptTo }} 集正文
                </h2>
                <div class="ss-card__actions">
                  <div class="ss-view-toggle" role="group" aria-label="预览或编辑">
                    <button
                      type="button"
                      class="ss-view-toggle__btn"
                      :class="{ 'ss-view-toggle__btn--on': docViewMode === 'preview' }"
                      @click="docViewMode = 'preview'"
                    >
                      预览
                    </button>
                    <button
                      type="button"
                      class="ss-view-toggle__btn"
                      :class="{ 'ss-view-toggle__btn--on': docViewMode === 'edit' }"
                      @click="docViewMode = 'edit'"
                    >
                      编辑
                    </button>
                  </div>
                  <button
                    type="button"
                    class="ss-btn ss-btn--sm"
                    :disabled="busy"
                    :title="scriptGenCreditTitle"
                    @click="onGenerateCurrent"
                  >
                    <i :class="busy ? 'fas fa-spinner fa-spin' : 'fas fa-sync'"></i>
                    {{ currentScriptBody ? '重新生成本批' : '生成本批正文' }}
                    <span v-if="!busy" class="ss-credit-badge ss-credit-badge--sm" aria-hidden="true">
                      <i class="fas fa-bolt"></i>{{ scriptGenCreditsText }}
                    </span>
                  </button>
                </div>
              </div>
              <div
                v-if="docViewMode === 'preview'"
                class="ss-md markdown-reply"
                v-html="renderMarkdown(currentScriptBody, '点击「生成本批正文」生成剧本…')"
              ></div>
              <textarea
                v-else
                v-model="currentScriptBody"
                class="ss-doc"
                placeholder="点击「生成本批正文」，DeepSeek 会按策划/大纲/角色/世界写出文学剧本…"
                spellcheck="false"
              ></textarea>
            </section>
          </template>

          <!-- 大纲 / 角色 / 世界 -->
          <template v-else>
            <section class="ss-card ss-card--doc">
              <div class="ss-card__head">
                <h2 class="ss-card__h">{{ currentSectionLabel }}</h2>
                <div class="ss-card__actions">
                  <div class="ss-view-toggle" role="group" aria-label="预览或编辑">
                    <button
                      type="button"
                      class="ss-view-toggle__btn"
                      :class="{ 'ss-view-toggle__btn--on': docViewMode === 'preview' }"
                      @click="docViewMode = 'preview'"
                    >
                      预览
                    </button>
                    <button
                      type="button"
                      class="ss-view-toggle__btn"
                      :class="{ 'ss-view-toggle__btn--on': docViewMode === 'edit' }"
                      @click="docViewMode = 'edit'"
                    >
                      编辑
                    </button>
                  </div>
                  <button
                    type="button"
                    class="ss-btn ss-btn--sm"
                    :disabled="busy"
                    :title="scriptGenCreditTitle"
                    @click="onGenerateCurrent"
                  >
                    <i :class="busy ? 'fas fa-spinner fa-spin' : 'fas fa-sync'"></i>
                    {{ draftDocEmpty ? 'AI 生成' : '重新生成' }}
                    <span v-if="!busy" class="ss-credit-badge ss-credit-badge--sm" aria-hidden="true">
                      <i class="fas fa-bolt"></i>{{ scriptGenCreditsText }}
                    </span>
                  </button>
                </div>
              </div>
              <div
                v-if="docViewMode === 'preview'"
                class="ss-md markdown-reply"
                v-html="renderMarkdown(draftDoc, docPlaceholder)"
              ></div>
              <textarea
                v-else
                v-model="draftDoc"
                class="ss-doc"
                :placeholder="docPlaceholder"
                spellcheck="false"
              ></textarea>
            </section>
          </template>
        </main>

        <!-- 右：封面 -->
        <aside class="ss-side">
          <p class="ss-side__label">剧本封面</p>
          <div class="ss-cover">
            <img v-if="draft.coverUrl" :src="draft.coverUrl" :alt="displayTitle" />
            <div v-else class="ss-cover__ph">
              <span>{{ coverInitials }}</span>
              <p>{{ displayTitle }}</p>
            </div>
          </div>
          <p class="ss-side__label ss-side__label--mt">封面提示词</p>
          <textarea
            v-model="draft.coverPrompt"
            class="ss-cover-prompt"
            rows="10"
            placeholder="生成策划后会自动写入电影海报级提示词，可手动修改"
          ></textarea>
        </aside>
      </div>

      <footer class="ss-foot">
        <button
          type="button"
          class="ss-foot__cta"
          :disabled="busy"
          :title="scriptGenCreditTitle"
          @click="onGenerateCurrent"
        >
          <span class="ss-cta__main">
            <i :class="busy ? 'fas fa-spinner fa-spin' : 'fas fa-wand-magic-sparkles'"></i>
            <span class="ss-cta__text">
              <span class="ss-cta__label">{{ generateLabel }}</span>
              <span v-if="generateSubLabel" class="ss-cta__sub">{{ generateSubLabel }}</span>
            </span>
          </span>
          <span v-if="!busy" class="ss-credit-badge ss-credit-badge--on-dark" aria-hidden="true">
            <i class="fas fa-bolt"></i>
            {{ scriptGenCreditsText }}
          </span>
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import PlusAppSidebar from '@/components/PlusAppSidebar.vue'
import { openLoginModal, hasAuthToken } from '@/utils/loginGate'
import { saveCanvasNodeHandoff } from '@/utils/canvasNodeHandoff'
import { showErrorToast, showSuccessToast } from '@/utils/toast'
import {
  SCRIPT_STUDIO_SECTIONS,
  SCRIPT_STUDIO_MARKETS,
  SCRIPT_STUDIO_CATEGORIES,
  SCRIPT_STUDIO_DEEPSEEK_MODEL,
  loadScriptStudioDraft,
  saveScriptStudioDraft,
  createEmptyScriptStudioDraft,
  generateScriptPlanning,
  generateScriptOutline,
  generateScriptCharacters,
  generateScriptWorld,
  generateScriptBody,
  applyPlanningJsonToDraft,
  buildScriptStudioExportMarkdown,
  buildEpisodeBatches,
  episodeRangeKey,
  getEpisodeBody,
  setEpisodeBody
} from '@/utils/scriptStudio'
import {
  createScriptStudioUuid,
  saveScriptStudioProject,
  fetchScriptStudioDetail,
  fetchScriptStudioList,
  deleteScriptStudioProject
} from '@/utils/scriptStudioApi'
import {
  NGC_MODEL_KIND,
  fetchModelsList,
  calcRequiredCreditsFromCatalog,
  assertUserHasCredits
} from '@/utils/modelsListApi'
import marked from 'marked'
import DOMPurify from 'dompurify'

marked.setOptions({ gfm: true, breaks: true })

export default {
  name: 'ScriptStudio',
  components: { PlusAppSidebar },
  data() {
    const draft = loadScriptStudioDraft()
    if (!draft.uuid) draft.uuid = createScriptStudioUuid()
    return {
      sections: SCRIPT_STUDIO_SECTIONS,
      markets: SCRIPT_STUDIO_MARKETS,
      categories: SCRIPT_STUDIO_CATEGORIES,
      sectionId: 'planning',
      draft,
      busy: false,
      cloudSaving: false,
      abortCtrl: null,
      saveTimer: null,
      cloudListOpen: false,
      cloudListLoading: false,
      cloudListError: '',
      cloudList: [],
      cloudListKeyword: '',
      cloudDeletingUuid: '',
      /** preview | edit */
      docViewMode: 'preview',
      /** 与画布剧本创作节点共用 modelslist 定价 */
      modelsCatalog: [],
      modelsCatalogLoading: false,
      /** light | dark，与全站 theme 同步（默认跟主站：dark） */
      theme: (localStorage.getItem('theme') || 'dark') === 'light' ? 'light' : 'dark'
    }
  },
  computed: {
    /** 与 NodeGraphCanvas.scriptWriteCreditDisplay 一致：deepseek-v4-flash 文本模型积分 */
    scriptGenCredits() {
      return calcRequiredCreditsFromCatalog(
        this.modelsCatalog,
        SCRIPT_STUDIO_DEEPSEEK_MODEL,
        NGC_MODEL_KIND.LLM,
        { times: 1 }
      )
    },
    scriptGenCreditsText() {
      if (this.modelsCatalogLoading) return '…'
      const c = Number(this.scriptGenCredits) || 0
      return c > 0 ? String(c) : '—'
    },
    scriptGenCreditTitle() {
      const c = Number(this.scriptGenCredits) || 0
      if (c > 0) {
        return `消耗 ${c} 积分（与画布「剧本创作」节点相同：${SCRIPT_STUDIO_DEEPSEEK_MODEL}）`
      }
      return `计费模型：${SCRIPT_STUDIO_DEEPSEEK_MODEL}（与画布剧本创作节点一致）`
    },
    displayTitle() {
      const t =
        (this.draft.plan && this.draft.plan.title) ||
        (this.draft.aiTopic && this.draft.aiTopic.title) ||
        ''
      return t.trim() || '未命名剧本'
    },
    coverInitials() {
      const t = this.displayTitle.replace(/[《》\s]/g, '')
      return (t.slice(0, 2) || '剧').toUpperCase()
    },
    hasAiTopic() {
      const t = this.draft.aiTopic || {}
      return !!(t.title || t.oneLiner || t.mainPlot)
    },
    episodeTotal() {
      return Math.max(1, Math.min(200, Number(this.draft.plan && this.draft.plan.episodeCount) || 40))
    },
    episodeBatches() {
      return buildEpisodeBatches(this.episodeTotal)
    },
    episodeBodyMap() {
      return (this.draft && this.draft.episodeBodies) || {}
    },
    normalizedScriptFrom() {
      const from = Math.max(1, Number(this.draft.scriptFrom) || 1)
      return Math.min(from, this.episodeTotal)
    },
    normalizedScriptTo() {
      const from = this.normalizedScriptFrom
      const to = Math.max(from, Number(this.draft.scriptTo) || from)
      return Math.min(to, this.episodeTotal)
    },
    scriptBatchId() {
      return episodeRangeKey(this.normalizedScriptFrom, this.normalizedScriptTo)
    },
    currentScriptBody: {
      get() {
        return getEpisodeBody(this.draft, this.normalizedScriptFrom, this.normalizedScriptTo)
      },
      set(v) {
        this.draft = setEpisodeBody(
          this.draft,
          this.normalizedScriptFrom,
          this.normalizedScriptTo,
          v
        )
      }
    },
    currentSectionLabel() {
      const hit = this.sections.find(s => s.id === this.sectionId)
      return hit ? hit.label : ''
    },
    draftDoc: {
      get() {
        if (this.sectionId === 'outline') return this.draft.outline
        if (this.sectionId === 'characters') return this.draft.characters
        if (this.sectionId === 'world') return this.draft.world
        return ''
      },
      set(v) {
        if (this.sectionId === 'outline') this.$set(this.draft, 'outline', v)
        else if (this.sectionId === 'characters') this.$set(this.draft, 'characters', v)
        else if (this.sectionId === 'world') this.$set(this.draft, 'world', v)
      }
    },
    draftDocEmpty() {
      return !String(this.draftDoc || '').trim()
    },
    docPlaceholder() {
      if (this.sectionId === 'outline') return '点击「AI 生成」产出分集大纲，也可直接粘贴编辑…'
      if (this.sectionId === 'characters') return '点击「AI 生成」产出角色小传与出图提示词…'
      return '点击「AI 生成」产出世界观、场景与视觉色板…'
    },
    generateLabel() {
      if (this.busy) return '生成中…'
      if (this.sectionId === 'planning') {
        return this.hasAiTopic ? '重新生成策划' : 'AI 生成策划'
      }
      if (this.sectionId === 'script') {
        return this.currentScriptBody ? '重新生成正文' : '生成正文'
      }
      return this.draftDocEmpty ? `生成${this.currentSectionLabel}` : `重新生成${this.currentSectionLabel}`
    },
    generateSubLabel() {
      if (this.busy || this.sectionId !== 'script') return ''
      const batch = String(this.scriptBatchId || '').trim()
      return batch ? `第 ${batch} 集` : ''
    },
    filteredCloudList() {
      const q = String(this.cloudListKeyword || '')
        .trim()
        .toLowerCase()
      const list = Array.isArray(this.cloudList) ? this.cloudList : []
      if (!q) return list
      return list.filter(row => String((row && row.title) || '').toLowerCase().includes(q))
    }
  },
  watch: {
    draft: {
      deep: true,
      handler() {
        this.scheduleSave()
      }
    },
    sectionId() {
      // 切到大纲/角色/世界/正文时默认看渲染预览
      if (this.sectionId !== 'planning') {
        this.docViewMode = 'preview'
      }
    }
  },
  mounted() {
    this.applyTheme(this.theme)
    this.loadModelsCatalog()
    const qid = this.$route && this.$route.query ? String(this.$route.query.uuid || '').trim() : ''
    if (qid) {
      this.loadCloudProject(qid)
    }
  },
  beforeDestroy() {
    if (this.saveTimer) clearTimeout(this.saveTimer)
    if (this.abortCtrl) this.abortCtrl.abort()
    saveScriptStudioDraft(this.draft)
  },
  methods: {
    renderMarkdown(raw, emptyHint) {
      const text = String(raw || '').trim()
      if (!text) {
        const hint = String(emptyHint || '暂无内容')
        return DOMPurify.sanitize(`<p class="ss-md__empty">${hint}</p>`)
      }
      try {
        const html = marked.parse(text)
        return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } })
      } catch {
        return DOMPurify.sanitize(`<pre class="ss-md__fallback">${text}</pre>`)
      }
    },
    scheduleSave() {
      if (this.saveTimer) clearTimeout(this.saveTimer)
      this.saveTimer = setTimeout(() => {
        saveScriptStudioDraft(this.draft)
      }, 400)
    },
    ensureProjectUuid() {
      if (!this.draft.uuid) {
        this.$set(this.draft, 'uuid', createScriptStudioUuid())
      }
      return this.draft.uuid
    },
    async onCloudSave() {
      if (this.cloudSaving || this.busy) return
      if (!this.ensureLogin()) return
      this.cloudSaving = true
      try {
        const uuid = this.ensureProjectUuid()
        const state = { ...this.draft }
        const saved = await saveScriptStudioProject({
          uuid,
          title: this.displayTitle,
          cover_prompt: this.draft.coverPrompt || '',
          state
        })
        // 回读校验，避免“假成功”
        await fetchScriptStudioDetail(saved.uuid || uuid)
        this.$set(this.draft, 'cloudSavedAt', new Date().toISOString())
        saveScriptStudioDraft(this.draft)
        showSuccessToast('已保存到云端')
        if (this.cloudListOpen) {
          await this.refreshCloudList()
        }
      } catch (e) {
        showErrorToast((e && e.message) || '云端保存失败')
      } finally {
        this.cloudSaving = false
      }
    },
    async onCloudSaveFromModal() {
      await this.onCloudSave()
    },
    openCloudList() {
      if (!this.ensureLogin()) return
      this.cloudListOpen = true
      this.refreshCloudList()
    },
    async refreshCloudList() {
      this.cloudListLoading = true
      this.cloudListError = ''
      try {
        const result = await fetchScriptStudioList({ limit: 50 })
        this.cloudList = (result && result.rows) || []
        if (result && result.listError) {
          this.cloudListError = result.listError
        } else if (!this.cloudList.length) {
          this.cloudListError = ''
        }
      } catch (e) {
        this.cloudList = []
        this.cloudListError = (e && e.message) || '加载列表失败'
      } finally {
        this.cloudListLoading = false
      }
    },
    cloudTitleInitials(title) {
      const t = String(title || '剧')
        .replace(/[《》\s]/g, '')
        .slice(0, 2)
      return t || '剧'
    },
    formatCloudTime(v) {
      if (!v) return ''
      const d = new Date(v)
      if (Number.isNaN(d.getTime())) return String(v)
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(
        d.getMinutes()
      )}`
    },
    async openCloudProject(row) {
      const uuid = row && row.uuid ? String(row.uuid).trim() : ''
      if (!uuid) return
      this.cloudListOpen = false
      await this.loadCloudProject(uuid)
      if (this.$router && this.$route.query.uuid !== uuid) {
        this.$router
          .replace({ path: '/script-studio', query: { uuid } })
          .catch(() => {})
      }
    },
    async deleteCloudProject(row) {
      const uuid = row && row.uuid ? String(row.uuid).trim() : ''
      if (!uuid) return
      const title = (row && row.title) || '未命名剧本'
      if (!window.confirm(`确定删除云端剧本「${title}」？此操作不可撤销。`)) return
      this.cloudDeletingUuid = uuid
      try {
        await deleteScriptStudioProject(uuid)
        this.cloudList = this.cloudList.filter(x => x.uuid !== uuid)
        if (this.draft.uuid === uuid) {
          this.$set(this.draft, 'cloudSavedAt', null)
        }
        showSuccessToast('已删除')
      } catch (e) {
        showErrorToast((e && e.message) || '删除失败')
      } finally {
        this.cloudDeletingUuid = ''
      }
    },
    async loadCloudProject(uuid) {
      if (!hasAuthToken()) return
      try {
        const detail = await fetchScriptStudioDetail(uuid)
        const state = detail.state || {}
        const empty = createEmptyScriptStudioDraft()
        this.draft = {
          ...empty,
          ...state,
          uuid: detail.uuid || uuid,
          aiTopic: { ...empty.aiTopic, ...(state.aiTopic || {}) },
          plan: { ...empty.plan, ...(state.plan || {}) },
          episodeBodies:
            state.episodeBodies && typeof state.episodeBodies === 'object'
              ? state.episodeBodies
              : {},
          cloudSavedAt: detail.updated_at || new Date().toISOString()
        }
        this.sectionId = 'planning'
        saveScriptStudioDraft(this.draft)
        showSuccessToast('已打开云端剧本')
      } catch (e) {
        showErrorToast((e && e.message) || '加载失败')
      }
    },
    syncGenresFromText() {
      const parts = String(this.draft.plan.genreText || '')
        .split(/[,，、/\s]+/)
        .map(x => x.trim())
        .filter(Boolean)
      this.$set(this.draft.plan, 'genres', parts)
    },
    selectEpisodeBatch(b) {
      if (!b) return
      this.sectionId = 'script'
      this.$set(this.draft, 'scriptFrom', b.from)
      this.$set(this.draft, 'scriptTo', b.to)
    },
    goHome() {
      this.$router.push('/').catch(() => {})
    },
    applyTheme(next) {
      const theme = next === 'dark' ? 'dark' : 'light'
      this.theme = theme
      try {
        localStorage.setItem('theme', theme)
      } catch {
        /* ignore */
      }
      if (typeof document !== 'undefined' && document.body) {
        document.body.setAttribute('data-theme', theme)
      }
    },
    toggleTheme() {
      this.applyTheme(this.theme === 'dark' ? 'light' : 'dark')
    },
    ensureLogin() {
      if (hasAuthToken()) return true
      openLoginModal()
      showErrorToast('请先登录')
      return false
    },
    async loadModelsCatalog() {
      if (this._modelsCatalogPromise) return this._modelsCatalogPromise
      this.modelsCatalogLoading = true
      this._modelsCatalogPromise = (async () => {
        try {
          this.modelsCatalog = await fetchModelsList()
        } catch {
          this.modelsCatalog = []
        } finally {
          this.modelsCatalogLoading = false
          this._modelsCatalogPromise = null
        }
      })()
      return this._modelsCatalogPromise
    },
    /** 与画布 ensureNgcScoreBeforeRun(scriptWrite) 一致：生成前校验积分 */
    async ensureScriptGenCredits(label) {
      if (!this.modelsCatalog.length) {
        await this.loadModelsCatalog()
      }
      const required = Number(this.scriptGenCredits) || 0
      if (required <= 0) return
      await assertUserHasCredits({
        required,
        label: label || '剧本生成'
      })
    },
    async onGenerateCurrent() {
      if (this.busy) return
      if (!this.ensureLogin()) return
      try {
        await this.ensureScriptGenCredits(this.generateLabel)
      } catch (e) {
        showErrorToast((e && e.message) || '积分不足~')
        return
      }
      this.busy = true
      if (this.abortCtrl) this.abortCtrl.abort()
      this.abortCtrl = typeof AbortController !== 'undefined' ? new AbortController() : null
      const signal = this.abortCtrl ? this.abortCtrl.signal : undefined
      try {
        if (this.sectionId === 'planning') {
          const json = await generateScriptPlanning(this.draft, signal)
          this.draft = applyPlanningJsonToDraft(this.draft, json)
          this.syncGenresFromText()
          showSuccessToast('剧本策划已生成')
        } else if (this.sectionId === 'outline') {
          const text = await generateScriptOutline(this.draft, signal)
          this.$set(this.draft, 'outline', text)
          showSuccessToast('剧本大纲已生成')
          this.docViewMode = 'preview'
        } else if (this.sectionId === 'characters') {
          const text = await generateScriptCharacters(this.draft, signal)
          this.$set(this.draft, 'characters', text)
          showSuccessToast('角色设定已生成')
          this.docViewMode = 'preview'
        } else if (this.sectionId === 'world') {
          const text = await generateScriptWorld(this.draft, signal)
          this.$set(this.draft, 'world', text)
          showSuccessToast('世界设定已生成')
          this.docViewMode = 'preview'
        } else if (this.sectionId === 'script') {
          const from = this.normalizedScriptFrom
          const to = this.normalizedScriptTo
          if (to - from > 4) {
            throw new Error('单次最多生成 5 集，请缩小范围或点左侧分批')
          }
          const text = await generateScriptBody(this.draft, { from, to }, signal)
          this.draft = setEpisodeBody(this.draft, from, to, text)
          showSuccessToast(`第 ${from}–${to} 集正文已生成`)
          this.docViewMode = 'preview'
        }
        this.ensureProjectUuid()
        saveScriptStudioDraft(this.draft)
      } catch (e) {
        if (e && e.name === 'AbortError') return
        showErrorToast((e && e.message) || '生成失败')
      } finally {
        this.busy = false
      }
    },
    onReset() {
      if (this.busy || this.cloudSaving) return
      if (!window.confirm('清空当前草稿并新建？此操作不可撤销。')) return
      this.draft = createEmptyScriptStudioDraft()
      this.draft.uuid = createScriptStudioUuid()
      this.sectionId = 'planning'
      saveScriptStudioDraft(this.draft)
      if (this.$route.query && this.$route.query.uuid) {
        this.$router.replace({ path: '/script-studio' }).catch(() => {})
      }
      showSuccessToast('已新建空白剧本')
    },
    onDownload() {
      const md = buildScriptStudioExportMarkdown(this.draft)
      const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${this.displayTitle || 'script'}.md`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      showSuccessToast('已导出 Markdown')
    },
    onSendToCanvas() {
      if (!this.ensureLogin()) return
      const md = buildScriptStudioExportMarkdown(this.draft)
      saveCanvasNodeHandoff({
        nodeType: 'scriptWrite',
        prompt: md,
        autoRun: false
      })
      showSuccessToast('已送到画布「剧本创作」节点')
      this.$router.push('/workspace').catch(() => {})
    }
  }
}
</script>

<style scoped>
.ss-shell {
  --ss-page: #f3f4f6;
  --ss-surface: #ffffff;
  --ss-surface-2: #fafafa;
  --ss-muted: #f3f4f6;
  --ss-soft: #f9fafb;
  --ss-hover: #f5f3ff;
  --ss-border: #e8e8ec;
  --ss-border-2: #e5e7eb;
  --ss-border-3: #f0f0f2;
  --ss-text: #111111;
  --ss-text-2: #374151;
  --ss-text-3: #4b5563;
  --ss-text-4: #6b7280;
  --ss-text-5: #9ca3af;
  --ss-accent: #7c3aed;
  --ss-accent-2: #6d28d9;
  --ss-accent-3: #5b21b6;
  --ss-accent-soft: #ede9fe;
  --ss-accent-softer: #f5f3ff;
  --ss-accent-border: #ddd6fe;
  --ss-accent-border-2: #c4b5fd;
  --ss-input-bg: #fafafa;
  --ss-input-focus-bg: #ffffff;
  --ss-focus-ring: rgba(139, 92, 246, 0.12);
  --ss-code-bg: #f3f4f6;
  --ss-pre-bg: #f8fafc;
  --ss-pre-border: #eef2f7;
  --ss-quote: #4b5563;
  --ss-hr: #eeeeee;
  --ss-success: #059669;
  --ss-success-2: #047857;
  --ss-success-border: #a7f3d0;
  --ss-warn: #b45309;
  --ss-warn-bg: #fff7ed;
  --ss-danger: #dc2626;
  --ss-danger-bg: #fef2f2;
  --ss-credit-sm-bg: #fff7ed;
  --ss-credit-sm-fg: #b45309;
  --ss-modal-mask: rgba(15, 15, 20, 0.45);
  --ss-foot-bg: rgba(255, 255, 255, 0.92);
  --ss-toggle-on-bg: #ffffff;
  --ss-shadow-card: 0 1px 0 rgba(0, 0, 0, 0.03);
  background: var(--ss-page);
  color: var(--ss-text);
  transition: background-color 0.22s ease, color 0.22s ease;
}

.ss-shell[data-theme='dark'] {
  --ss-page: #0b0b10;
  --ss-surface: #14141b;
  --ss-surface-2: #1a1a22;
  --ss-muted: #1c1c24;
  --ss-soft: #18181f;
  --ss-hover: rgba(139, 92, 246, 0.14);
  --ss-border: #2a2a35;
  --ss-border-2: #32323f;
  --ss-border-3: #26262f;
  --ss-text: #f3f4f6;
  --ss-text-2: #e5e7eb;
  --ss-text-3: #d1d5db;
  --ss-text-4: #9ca3af;
  --ss-text-5: #6b7280;
  --ss-accent: #a78bfa;
  --ss-accent-2: #8b5cf6;
  --ss-accent-3: #c4b5fd;
  --ss-accent-soft: rgba(139, 92, 246, 0.22);
  --ss-accent-softer: rgba(139, 92, 246, 0.12);
  --ss-accent-border: rgba(167, 139, 250, 0.35);
  --ss-accent-border-2: rgba(167, 139, 250, 0.5);
  --ss-input-bg: #1a1a22;
  --ss-input-focus-bg: #1f1f28;
  --ss-focus-ring: rgba(139, 92, 246, 0.22);
  --ss-code-bg: #1c1c24;
  --ss-pre-bg: #121218;
  --ss-pre-border: #2a2a35;
  --ss-quote: #9ca3af;
  --ss-hr: #2a2a35;
  --ss-success: #34d399;
  --ss-success-2: #6ee7b7;
  --ss-success-border: rgba(52, 211, 153, 0.35);
  --ss-warn: #fbbf24;
  --ss-warn-bg: rgba(251, 191, 36, 0.12);
  --ss-danger: #f87171;
  --ss-danger-bg: rgba(248, 113, 113, 0.12);
  --ss-credit-sm-bg: rgba(251, 191, 36, 0.14);
  --ss-credit-sm-fg: #fbbf24;
  --ss-modal-mask: rgba(0, 0, 0, 0.62);
  --ss-foot-bg: rgba(14, 14, 20, 0.92);
  --ss-toggle-on-bg: #22222c;
  --ss-shadow-card: 0 1px 0 rgba(255, 255, 255, 0.04);
}

.ss-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  background: var(--ss-surface);
  border-bottom: 1px solid var(--ss-border);
  color: var(--ss-text);
}

.ss-topbar__left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.ss-topbar__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
  color: var(--ss-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ss-topbar__sub {
  margin: 2px 0 0;
  font-size: 12px;
  color: var(--ss-text-5);
}

.ss-topbar__saved {
  margin-left: 8px;
  color: var(--ss-accent);
}

.ss-icon-btn {
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 10px;
  background: var(--ss-muted);
  color: var(--ss-text-3);
  cursor: pointer;
}

.ss-icon-btn:hover {
  background: var(--ss-accent-soft);
  color: var(--ss-accent-2);
}

.ss-theme-btn i {
  font-size: 14px;
}

.ss-shell[data-theme='dark'] .ss-theme-btn i.fa-sun {
  color: #fbbf24;
}

.ss-topbar__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
}

.ss-btn {
  height: 34px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid transparent;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #7c3aed;
  color: #fff;
}

.ss-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.ss-btn--ghost {
  background: var(--ss-muted);
  color: var(--ss-text-2);
}

.ss-btn--outline {
  background: var(--ss-surface);
  border-color: var(--ss-accent-border);
  color: var(--ss-accent-2);
}

.ss-btn--sm {
  height: 30px;
  font-size: 12px;
  padding: 0 10px;
}

.ss-body {
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr) 280px;
  min-height: 0;
}

.ss-nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 12px;
  background: var(--ss-surface);
  border-right: 1px solid var(--ss-border);
  color: var(--ss-text);
  min-height: 0;
}

.ss-nav__label {
  margin: 0 8px 8px;
  font-size: 12px;
  font-weight: 700;
  color: var(--ss-text-5);
}

.ss-nav__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  border: none;
  background: transparent;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  color: var(--ss-text-3);
  cursor: pointer;
  text-align: left;
}

.ss-nav__item i {
  width: 16px;
  text-align: center;
  color: var(--ss-text-5);
}

.ss-nav__item:hover {
  background: var(--ss-hover);
  color: var(--ss-accent-3);
}

.ss-nav__item--on {
  background: var(--ss-accent-soft);
  color: var(--ss-accent-3);
}

.ss-nav__item--on i {
  color: var(--ss-accent);
}

.ss-nav__hint {
  margin: 16px 8px 0;
  padding: 12px;
  border-radius: 12px;
  background: var(--ss-soft);
  font-size: 12px;
  color: var(--ss-text-4);
}

.ss-nav__hint p {
  margin: 0 0 6px;
  font-weight: 700;
  color: var(--ss-text-2);
}

.ss-nav__hint ol {
  margin: 0;
  padding-left: 18px;
  line-height: 1.7;
}

.ss-nav__batches {
  margin-top: 12px;
  max-height: 220px;
  overflow-y: auto;
  padding-right: 2px;
}

.ss-nav__item--batch {
  font-size: 12px;
  padding: 8px 10px;
}

.ss-nav__item--done i {
  color: var(--ss-success);
}

.ss-batch-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.ss-chip {
  border: 1px solid var(--ss-border-2);
  background: var(--ss-surface);
  color: var(--ss-text-3);
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.ss-chip--on {
  border-color: var(--ss-accent-border-2);
  background: var(--ss-accent-soft);
  color: var(--ss-accent-3);
}

.ss-chip--done {
  border-color: var(--ss-success-border);
  color: var(--ss-success-2);
}

.ss-script-tip {
  margin: 10px 0 0;
  font-size: 12px;
  color: var(--ss-text-5);
  line-height: 1.5;
}

.ss-nav__foot {
  margin-top: auto;
  padding-top: 12px;
}

.ss-cta {
  width: 100%;
  min-height: 44px;
  height: auto;
  padding: 10px 12px;
  border: none;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  box-sizing: border-box;
}

.ss-cta__main {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1 1 auto;
}

.ss-cta__text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
}

.ss-cta__label {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  line-height: 1.2;
}

.ss-cta__sub {
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  opacity: 0.78;
  white-space: nowrap;
}

.ss-cta--primary {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  color: #fff;
  box-shadow: 0 8px 20px rgba(124, 58, 237, 0.28);
}

.ss-cta:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ss-credit-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  flex: 0 0 auto;
  margin-left: 0;
  min-width: 36px;
  height: 24px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  background: rgba(0, 0, 0, 0.18);
  color: #fef3c7;
  line-height: 1;
}

.ss-credit-badge i {
  font-size: 10px;
  color: #fbbf24;
}

.ss-credit-badge--on-dark {
  background: rgba(0, 0, 0, 0.2);
}

.ss-credit-badge--sm {
  margin-left: 4px;
  min-width: 0;
  height: 20px;
  padding: 0 6px;
  font-size: 10px;
  background: var(--ss-credit-sm-bg);
  color: var(--ss-credit-sm-fg);
}

.ss-credit-badge--sm i {
  color: #f59e0b;
}

.ss-main {
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  color: var(--ss-text);
}

.ss-card {
  background: var(--ss-surface);
  border-radius: 16px;
  padding: 16px 18px;
  margin-bottom: 14px;
  box-shadow: var(--ss-shadow-card);
  border: 1px solid transparent;
}

.ss-shell[data-theme='dark'] .ss-card {
  border-color: var(--ss-border-3);
}

.ss-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.ss-card__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.ss-view-toggle {
  display: inline-flex;
  padding: 2px;
  border-radius: 999px;
  background: var(--ss-muted);
  border: 1px solid var(--ss-border-2);
}

.ss-view-toggle__btn {
  border: none;
  background: transparent;
  color: var(--ss-text-4);
  font-size: 12px;
  font-weight: 700;
  padding: 5px 10px;
  border-radius: 999px;
  cursor: pointer;
}

.ss-view-toggle__btn--on {
  background: var(--ss-toggle-on-bg);
  color: var(--ss-accent-3);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.ss-md {
  min-height: 520px;
  max-height: none;
  overflow: auto;
  padding: 14px 16px 20px;
  border: 1px solid var(--ss-border-2);
  border-radius: 10px;
  background: var(--ss-surface);
  color: var(--ss-text-2);
  font-size: 14px;
  line-height: 1.75;
}

.ss-md :deep(h1),
.ss-md :deep(h2),
.ss-md :deep(h3),
.ss-md :deep(h4) {
  margin: 1.1em 0 0.45em;
  font-weight: 800;
  color: var(--ss-text);
  line-height: 1.35;
}

.ss-md :deep(h1) { font-size: 1.35rem; }
.ss-md :deep(h2) { font-size: 1.15rem; }
.ss-md :deep(h3) { font-size: 1.02rem; }

.ss-md :deep(h1:first-child),
.ss-md :deep(h2:first-child),
.ss-md :deep(h3:first-child) {
  margin-top: 0;
}

.ss-md :deep(p) { margin: 0.55em 0; }

.ss-md :deep(ul),
.ss-md :deep(ol) {
  margin: 0.45em 0 0.7em;
  padding-left: 1.35em;
}

.ss-md :deep(li) { margin: 0.25em 0; }

.ss-md :deep(strong) {
  font-weight: 800;
  color: var(--ss-text);
}

.ss-md :deep(code) {
  font-size: 0.9em;
  background: var(--ss-code-bg);
  padding: 0.1em 0.35em;
  border-radius: 4px;
}

.ss-md :deep(pre) {
  overflow: auto;
  padding: 10px 12px;
  border-radius: 8px;
  background: var(--ss-pre-bg);
  border: 1px solid var(--ss-pre-border);
}

.ss-md :deep(blockquote) {
  margin: 0.7em 0;
  padding: 0.2em 0 0.2em 0.9em;
  border-left: 3px solid var(--ss-accent-border-2);
  color: var(--ss-quote);
}

.ss-md :deep(hr) {
  border: none;
  border-top: 1px solid var(--ss-hr);
  margin: 1.2em 0;
}

.ss-md :deep(.ss-md__empty) {
  margin: 48px 0;
  text-align: center;
  color: var(--ss-text-5);
  font-size: 13px;
}

.ss-md :deep(.ss-md__fallback) {
  white-space: pre-wrap;
  font-family: inherit;
  margin: 0;
}

.ss-card__h {
  margin: 0 0 12px;
  font-size: 1rem;
  font-weight: 800;
  color: var(--ss-text);
}

.ss-card__head .ss-card__h {
  margin-bottom: 0;
}

.ss-badge {
  font-size: 11px;
  font-weight: 700;
  color: var(--ss-accent);
  background: var(--ss-accent-softer);
  border-radius: 999px;
  padding: 3px 10px;
}

.ss-topic {
  margin: 0;
  padding-left: 18px;
  line-height: 1.75;
  font-size: 13px;
  color: var(--ss-text-2);
}

.ss-topic strong {
  color: var(--ss-text);
}

.ss-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ss-form__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.ss-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: var(--ss-text-4);
}

.ss-field--block {
  width: 100%;
}

.ss-input,
.ss-textarea,
.ss-doc,
.ss-cover-prompt {
  width: 100%;
  border: 1px solid var(--ss-border-2);
  border-radius: 10px;
  background: var(--ss-input-bg);
  color: var(--ss-text);
  font-size: 13px;
  font-weight: 500;
  padding: 9px 12px;
  outline: none;
  font-family: inherit;
}

.ss-input:focus,
.ss-textarea:focus,
.ss-doc:focus,
.ss-cover-prompt:focus {
  border-color: var(--ss-accent-border-2);
  background: var(--ss-input-focus-bg);
  box-shadow: 0 0 0 3px var(--ss-focus-ring);
}

.ss-textarea {
  resize: vertical;
  min-height: 72px;
}

.ss-input-unit,
.ss-range {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ss-input-unit em,
.ss-range em {
  font-style: normal;
  font-size: 12px;
  color: var(--ss-text-5);
  flex-shrink: 0;
}

.ss-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.ss-tag {
  font-size: 11px;
  font-weight: 700;
  color: var(--ss-accent-2);
  background: var(--ss-accent-soft);
  border-radius: 999px;
  padding: 3px 10px;
}

.ss-card--doc {
  min-height: calc(100% - 8px);
  display: flex;
  flex-direction: column;
}

.ss-doc {
  flex: 1;
  min-height: 520px;
  resize: vertical;
  line-height: 1.7;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px;
}

.ss-side {
  min-height: 0;
  overflow-y: auto;
  padding: 14px;
  background: var(--ss-surface);
  border-left: 1px solid var(--ss-border);
  color: var(--ss-text);
}

.ss-side__label {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 800;
  color: var(--ss-text-4);
}

.ss-side__label--mt {
  margin-top: 14px;
}

.ss-cover {
  border-radius: 14px;
  overflow: hidden;
  background: #111;
  aspect-ratio: 3 / 4;
}

.ss-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ss-cover__ph {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background:
    radial-gradient(circle at 30% 20%, rgba(251, 191, 36, 0.35), transparent 45%),
    linear-gradient(160deg, #1f1135 0%, #0b0b10 55%, #3b0764 100%);
  color: #fff;
  padding: 16px;
  text-align: center;
}

.ss-cover__ph span {
  width: 64px;
  height: 64px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 800;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
}

.ss-cover__ph p {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  opacity: 0.9;
}

.ss-cover-prompt {
  min-height: 180px;
  resize: vertical;
  line-height: 1.55;
}

.ss-foot {
  display: none;
  padding: 10px 14px calc(10px + env(safe-area-inset-bottom));
  background: var(--ss-foot-bg);
  border-top: 1px solid var(--ss-border);
  backdrop-filter: blur(10px);
}

.ss-foot__cta {
  width: 100%;
  min-height: 46px;
  height: auto;
  padding: 10px 14px;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #8b5cf6, #6d28d9);
  color: #fff;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  box-sizing: border-box;
  box-shadow: 0 10px 28px rgba(109, 40, 217, 0.3);
}

.ss-foot__cta:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ss-modal {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: var(--ss-modal-mask);
  backdrop-filter: blur(6px);
}

.ss-modal__panel {
  width: min(560px, 100%);
  max-height: min(720px, 88vh);
  display: flex;
  flex-direction: column;
  background: var(--ss-surface);
  color: var(--ss-text);
  border-radius: 18px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.28);
  overflow: hidden;
  border: 1px solid var(--ss-border);
}

.ss-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px 10px;
  border-bottom: 1px solid var(--ss-border-3);
}

.ss-modal__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 800;
}

.ss-modal__sub {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--ss-text-5);
}

.ss-modal__toolbar {
  display: flex;
  gap: 8px;
  padding: 12px 18px;
}

.ss-modal__search {
  flex: 1;
  background: var(--ss-soft);
}

.ss-modal__body {
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  padding: 0 12px 16px;
}

.ss-modal__empty {
  padding: 36px 12px;
  text-align: center;
  color: var(--ss-text-5);
  font-size: 13px;
}

.ss-modal__empty--err {
  color: var(--ss-warn);
  line-height: 1.6;
  white-space: pre-wrap;
}

.ss-modal__hint {
  margin: 10px auto 0;
  max-width: 360px;
  font-size: 12px;
  line-height: 1.6;
  color: var(--ss-text-5);
}

.ss-modal__hint code {
  font-size: 11px;
  color: var(--ss-accent-2);
  background: var(--ss-accent-softer);
  padding: 1px 6px;
  border-radius: 6px;
}

.ss-modal__save-now {
  margin-top: 14px;
}

.ss-cloud-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ss-cloud-item {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--ss-border-2);
  border-radius: 14px;
  background: var(--ss-surface-2);
  overflow: hidden;
}

.ss-cloud-item__main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.ss-cloud-item__main:hover {
  background: var(--ss-hover);
}

.ss-cloud-item__thumb {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
  color: #fff;
  background: linear-gradient(135deg, #8b5cf6, #6d28d9);
}

.ss-cloud-item__meta {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ss-cloud-item__title {
  font-size: 14px;
  font-weight: 700;
  color: var(--ss-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ss-cloud-item__time {
  font-size: 11px;
  color: var(--ss-text-5);
}

.ss-cloud-item__del {
  width: 40px;
  height: 40px;
  margin-right: 6px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: var(--ss-text-5);
  cursor: pointer;
}

.ss-cloud-item__del:hover {
  background: var(--ss-danger-bg);
  color: var(--ss-danger);
}

@media (max-width: 1100px) {
  .ss-body {
    grid-template-columns: 180px minmax(0, 1fr);
  }
  .ss-side {
    display: none;
  }
}

@media (max-width: 820px) {
  .ss-body {
    grid-template-columns: 1fr;
  }
  .ss-nav {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    border-right: none;
    border-bottom: 1px solid var(--ss-border);
  }
  .ss-nav__label,
  .ss-nav__hint {
    display: none;
  }
  .ss-nav__item {
    width: auto;
    padding: 8px 12px;
  }
  .ss-nav__foot {
    display: none;
  }
  .ss-form__row {
    grid-template-columns: 1fr;
  }
  .ss-foot {
    display: block;
  }
  .ss-topbar__actions .ss-btn--outline span,
  .ss-topbar__actions .ss-btn--ghost {
    font-size: 12px;
  }
}
</style>
